import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import axiosInstance from '../../../api/axiosInstance';
import { createSalesInterview, updateSalesInterview } from '../../../api/sales';
import { formatDate, getUserInfoDetails } from '../../../utlis';
import useFetch from '../../../hooks/useFetch';
import StarRating from '../../../components/StarRating';
import SalesInterviewsHeader from './components/SalesInterviewsHeader';
import SalesInterviewsTable from './components/SalesInterviewsTable';
import SalesInterviewsModal from './components/SalesInterviewsModal';
import {
  initialSalesInterview,
  filterCategories,
  prepareInterviewForEdit,
  prepareInterviewPayload,
} from './salesInterviewUtils';

const { companyId, clientId } = getUserInfoDetails();

const SalesInterviews = () => {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: initialSalesInterview,
  });

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Loading states
  const [isLoading, setLoading] = useState(false);
  const [isSubmitting, setSubmitting] = useState(false);

  // Data state
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState('');
  const [pageSize, setPageSize] = useState(15);
  const [searchTerm, setSearchTerm] = useState('');

  // Filter state
  const [isFilterPopupOpen, setFilterPopupOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState([]);

  // Reference data
  const { data: refCandidate, loading: refCandidateLoading } = useFetch('/candidates');
  const { data: refVendor, loading: refVendorLoading } = useFetch('/vendors');
  const { data: refJobs, loading: refJobsLoading } = useFetch('/jobs');
  const { data: refTechnologies, loading: refTechnologiesLoading } = useFetch('/technology', {
    company: companyId,
    client: clientId,
  });

  useEffect(() => {
    if (refJobs && !refJobsLoading) {
      setJobs(refJobs.data.records);
    }
  }, [refJobs, refJobsLoading]);

  // Modal handlers
  const openModal = () => {
    reset(initialSalesInterview);
    setSelectedJob('');
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    reset(initialSalesInterview);
    setSelectedJob('');
  };

  const prepareUpdate = (recordId) => {
    try {
      const interview = data.find((d) => d.record_id === recordId);
      if (!interview) {
        throw new Error('Interview not found');
      }

      const preparedData = prepareInterviewForEdit(interview);
      setSelectedJob(interview.job_id);
      // Fallback to job record if interview doesn't have masterJobId/marketingId
      const jobRecord = jobs.find((j) => String(j.job_id) === String(interview.job_id));
      if (jobRecord) {
        if (!preparedData.masterJobId) {
          preparedData.masterJobId = jobRecord.master_job_id || '';
        }
        if (!preparedData.marketingId) {
          preparedData.marketingId = jobRecord.marketing_id || jobRecord.record_id || '';
        }
      }
      reset(preparedData);
      setIsModalOpen(true);
    } catch (error) {
      console.error('Error in prepareUpdate:', error);
      toast.error(error.message || 'An error occurred while preparing the update');
    }
  };

  // Job change handler
  const handleJobChange = (event) => {
    const jobId = event.target.value;
    setSelectedJob(jobId);
    const selectedJobDetails = jobs.find((job) => String(job.job_id) === String(jobId));
    if (selectedJobDetails) {
      setValue('jobTitle', selectedJobDetails.job_title);
      setValue('technology', selectedJobDetails.technology_id);
      setValue('vendor', selectedJobDetails.vendor_id);
      setValue('clientCompany', selectedJobDetails.company);
      setValue('clientName', selectedJobDetails.client);
      setValue('masterJobId', selectedJobDetails.master_job_id || '');
      setValue('marketingId', selectedJobDetails.marketing_id || selectedJobDetails.record_id || '');
    } else {
      reset({
        ...initialSalesInterview,
        jobTitle: '',
        technology: '',
        vendor: '',
        clientCompany: '',
        clientName: '',
      });
    }
  };

  // Form submission
  const onSubmit = async (formData) => {
    setSubmitting(true);
    try {
      const payload = prepareInterviewPayload(formData);

      if (formData.interviewId) {
        await updateSalesInterview(payload);
        toast.success('Interview Updated Successfully');
      } else {
        await createSalesInterview(payload);
        toast.success('Interview Added Successfully');
      }
      closeModal();
      fetchInterviews();
    } catch (error) {
      toast.error('Something Went Wrong');
    } finally {
      setSubmitting(false);
    }
  };

  // Fetch data
  const fetchInterviews = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/interview?client=${clientId}&company=${companyId}`);
      setData(res.data.data.records);
    } catch (err) {
      toast.error('Something Went Wrong');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInterviews();
  }, []);

  // Search and filter handlers
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterClick = () => {
    setFilterPopupOpen(true);
  };

  const handleCloseFilterPopup = () => {
    setFilterPopupOpen(false);
  };

  const handleApplyFilters = (filters) => {
    setAppliedFilters(filters || []);
    setFilterPopupOpen(false);
  };

  // Filter search results for FilterPopup
  const getSearchResults = (searchTerm, selectedCategory) => {
    if (!searchTerm || !selectedCategory || !data) return [];

    return data
      .filter((item) => {
        let valueToSearch = '';

        switch (selectedCategory) {
          case 'Job':
            valueToSearch = item.job_title || '';
            break;
          case 'Candidate':
            valueToSearch = item.candidate_name || '';
            break;
          case 'Client':
            valueToSearch = item.client || '';
            break;
          case 'Vendor':
            valueToSearch = item.vendor_name || '';
            break;
          case 'Bill Rate':
            valueToSearch = item.bill_rate?.toString() || '';
            break;
          case 'Interviewer':
            valueToSearch = item.interview_panel_details || '';
            break;
          case 'Rating':
            valueToSearch = item.rating?.toString() || '';
            break;
          case 'Feedback':
            valueToSearch = item.feedback || '';
            break;
          default:
            valueToSearch = '';
        }

        return valueToSearch.toLowerCase().includes(searchTerm.toLowerCase());
      })
      .map((item) => {
        switch (selectedCategory) {
          case 'Job':
            return item.job_title;
          case 'Candidate':
            return item.candidate_name;
          case 'Client':
            return item.client;
          case 'Vendor':
            return item.vendor_name;
          case 'Bill Rate':
            return item.bill_rate?.toString();
          case 'Interviewer':
            return item.interview_panel_details;
          case 'Rating':
            return item.rating?.toString();
          case 'Feedback':
            return item.feedback;
          default:
            return null;
        }
      })
      .filter(Boolean)
      .filter((value, index, self) => self.indexOf(value) === index);
  };

  // Apply search and filters to data
  useEffect(() => {
    if (!data.length) {
      setFilteredData([]);
      return;
    }

    let result = data;

    // Apply search filter first
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter((item) => {
        return (
          (item.job_title || '').toLowerCase().includes(searchLower) ||
          (item.candidate_name || '').toLowerCase().includes(searchLower) ||
          (item.client || '').toLowerCase().includes(searchLower) ||
          (item.vendor_name || '').toLowerCase().includes(searchLower) ||
          (item.interview_panel_details || '').toLowerCase().includes(searchLower) ||
          (item.feedback || '').toLowerCase().includes(searchLower)
        );
      });
    }

    // Apply category filters
    if (Array.isArray(appliedFilters) && appliedFilters.length > 0) {
      result = result.filter((item) => {
        return appliedFilters.some(({ category, items }) => {
          if (!items || items.length === 0) return false;

          switch (category) {
            case 'Job':
              return items.some((title) => title.toLowerCase() === (item.job_title || '').toLowerCase());
            case 'Candidate':
              return items.some((name) => name.toLowerCase() === (item.candidate_name || '').toLowerCase());
            case 'Client':
              return items.some((client) => client.toLowerCase() === (item.client || '').toLowerCase());
            case 'Vendor':
              return items.some((vendor) => vendor.toLowerCase() === (item.vendor_name || '').toLowerCase());
            case 'Bill Rate':
              return items.some((rate) => rate === item.bill_rate?.toString());
            case 'Interviewer':
              return items.some((interviewer) =>
                (item.interview_panel_details || '').toLowerCase().includes(interviewer.toLowerCase())
              );
            case 'Rating':
              return items.some((rating) => rating === item.rating?.toString());
            case 'Feedback':
              return items.some((feedback) =>
                (item.feedback || '').toLowerCase().includes(feedback.toLowerCase())
              );
            default:
              return false;
          }
        });
      });
    }

    setFilteredData(result);
  }, [appliedFilters, data, searchTerm]);

  // Table columns configuration
  const columns = [
    {
      name: 'record_id',
      label: 'ID',
      options: { display: false },
    },
    {
      name: 'job_title',
      label: 'Job',
      options: {
        customBodyRender: (value, tableMeta) => (
          <h3
            onClick={() => prepareUpdate(tableMeta.rowData[0])}
            className="text-base font-semibold text-auxiliary-800 cursor-pointer"
          >
            {value}
          </h3>
        ),
      },
    },
    {
      name: 'candidate_name',
      label: 'Candidate',
      options: { filter: true, sort: true },
    },
    {
      name: 'client',
      label: 'Client',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value, tableMeta) => {
          const client = tableMeta.rowData[tableMeta.columnIndex];
          const company = tableMeta.rowData[tableMeta.columnIndex + 1];
          return (
            <>
              <h3>{client}</h3>
              <p className="text-xs text-text-hint">{company}</p>
            </>
          );
        },
      },
    },
    {
      name: 'vendor_name',
      label: 'Vendor',
      options: { filter: true, sort: true },
    },
    {
      name: 'created',
      label: 'Created on',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => formatDate(value),
      },
    },
    {
      name: 'bill_rate',
      label: 'Bill Rate',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => `${value} USD`,
      },
    },
    {
      name: 'interview_panel_details',
      label: 'Interviewer',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          if (!value) return '';
          const truncatedValue =
            value.split(' ').slice(0, 2).join(' ') + (value.split(' ').length > 2 ? '...' : '');
          return <span title={value}>{truncatedValue}</span>;
        },
      },
    },
    {
      name: 'rating',
      label: 'Rating',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => <StarRating fontSize={16} initialRating={parseInt(value) || 0} />,
      },
    },
    {
      name: 'feedback',
      label: 'FeedBack',
      options: { filter: true, sort: true },
    },
  ];

  // Table options
  const options = {
    filterType: 'dropdown',
    responsive: 'standard',
    rowsPerPage: pageSize,
    rowsPerPageOptions: [5, 10, 15, 20, 50],
    onChangeRowsPerPage: (numberOfRows) => setPageSize(numberOfRows),
    selectableRows: 'none',
    elevation: 0,
    filter: true,
    search: false,
    download: true,
    print: true,
    viewColumns: true,
    textLabels: {
      body: {
        noMatch: !isLoading && (!filteredData || filteredData.length === 0) ? 'Sorry, no matching found' : '',
      },
    },
  };

  return (
    <div className="p-5">
      <div className="border border-gray-300 rounded-lg relative">
        {/* Header */}
        <SalesInterviewsHeader
          onReload={fetchInterviews}
          onAddInterview={openModal}
          isLoading={isLoading}
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
        />

        {/* Table with loading overlay */}
        <div className="relative">
          <SalesInterviewsTable
            data={isLoading ? [] : filteredData}
            columns={columns}
            options={options}
            isFilterPopupOpen={isFilterPopupOpen}
            onFilterClick={handleFilterClick}
            onCloseFilterPopup={handleCloseFilterPopup}
            onApplyFilters={handleApplyFilters}
            getSearchResults={getSearchResults}
            filterCategories={filterCategories}
          />
          {isLoading && (
            <div
              className="absolute left-0 right-0 flex flex-col items-center justify-center z-20"
              style={{ top: '64px', bottom: 0, background: 'rgba(255,255,255,0.9)' }}
            >
              <div className="w-10 h-10 border-4 border-secondary-200 border-t-secondary-700 rounded-full animate-spin mb-3"></div>
              <span className="text-lg text-secondary-700 font-semibold flex items-center">
                Loading
                <span className="inline-flex ml-1">
                  <span className="animate-bounce" style={{ animationDelay: '0ms' }}>
                    .
                  </span>
                  <span className="animate-bounce" style={{ animationDelay: '150ms' }}>
                    .
                  </span>
                  <span className="animate-bounce" style={{ animationDelay: '300ms' }}>
                    .
                  </span>
                </span>
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Interview Modal */}
      <SalesInterviewsModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onSubmit={onSubmit}
        register={register}
        errors={errors}
        handleSubmit={handleSubmit}
        setValue={setValue}
        watch={watch}
        isSubmitting={isSubmitting}
        refCandidate={refCandidate}
        refCandidateLoading={refCandidateLoading}
        refVendor={refVendor}
        refVendorLoading={refVendorLoading}
        refTechnologies={refTechnologies}
        refTechnologiesLoading={refTechnologiesLoading}
        jobs={jobs}
        selectedJob={selectedJob}
        onJobChange={handleJobChange}
      />
    </div>
  );
};

export default SalesInterviews;
